/**
 * Which right opens which Settings tab, and therefore Settings itself.
 *
 * Kept apart from the tab registries on purpose: those import panels and icons,
 * and this list has to be readable by `crm/nav.ts` - a module the sidebar and
 * the command palette load on every screen. Plain strings, no React, testable
 * on its own.
 *
 * Every entry below is the permission the SERVER checks on the routes that tab
 * talks to. That is the whole rule: a tab whose panel can only ever answer 403
 * is worse than no tab, because the operator reads a 403 as "broken" rather
 * than as "not yours". The lists are any-of.
 */

/**
 * The Integrations side menu.
 *
 * The first three used to carry no permission at all, which read as "anyone who
 * can open Settings". That was true only because Settings itself sat behind
 * `manage:ariregister`; once the door opens to the mail and call desks the
 * omission would have handed them three panels that 403 on the first GET.
 *
 * The nine phone tabs used to sit under this same key. They are their own
 * Settings tab now - see `PHONE_TAB_PERM` below. Card payments left the same
 * way and for a related reason: this menu is about outside services the CRM
 * reads FROM - the register, the mailbox, the browser - and money coming in was
 * the one entry that never fitted that sentence. See `PAYMENT_TAB_PERM`.
 */
export const INTEGRATION_TAB_PERM = {
  // GET and PUT /api/admin/crm/settings (ariregister/crm-settings.routes.ts).
  ariregister: "manage:ariregister",
  // GET /api/admin/crm/ariregister/licence (ariregister/licence.routes.ts).
  //
  // The READ right, not the write one, and for a reason the other tabs do not
  // have: this panel is where an operator finds out why an import refused to
  // start. Somebody who can look at the mirror has to be able to read that
  // answer. Accepting and withdrawing are `manage:ariregister` on the server
  // and refuse on their own.
  "open-data": "view:ariregister",
  // PUT /api/admin/crm/maps/settings (crm-maps/settings.routes.ts). The register
  // right rather than a new one: the map is a view of the register, and the key
  // it draws with belongs to whoever holds that mandate. The matching GET is
  // deliberately wider (`view:ariregister`) because every operator who opens the
  // map needs the key to load it - see the route's own comment.
  maps: "manage:ariregister",
  // GET and PUT /api/admin/ariregister/webrisk (ariregister/webrisk.routes.ts).
  // The register right for the same reason `maps` takes it: the blocklist is
  // only ever asked about a prospect, so it answers to the prospecting mandate.
  "web-risk": "manage:ariregister",
  // GET and PUT /api/admin/crm/monitoring/keys (crm-monitoring/keys.routes.ts).
  // Saidiseire's own right, not the register one: these keys look at our
  // customers' sites for the maintenance packages, not at prospects, and the
  // person who runs the monitoring is the one who pastes them.
  pagespeed: "manage:crm.monitoring",
  wordfence: "manage:crm.monitoring",
  // GET /api/admin/crm/browser/settings and /browser/cost (crm-browser). The
  // register right rather than a new one, the same choice `maps` made above and
  // for the same reason: the browser is here to look at prospects' sites, so it
  // answers to the prospecting mandate.
  //
  // The READ right, not the write one, and deliberately so. The panel's first
  // job is to say whether a credential exists at all, and a tab that will not
  // answer that is a tab that cannot explain why the brain came back without
  // screenshots. Saving and testing are `manage:ariregister` on the server and
  // refuse on their own.
  browser: "view:ariregister",
  // GET and PUT /api/admin/crm/mail/settings (crm-email/mail-settings.routes.ts).
  mail: "manage:crm.email",
  // GET and PUT /api/admin/crm/mail/channels (crm-email/channel-settings.routes.ts).
  channels: "manage:crm.email",
  // GET and PUT /api/admin/crm/mail/guard (crm-email/guard.routes.ts).
  "test-mode": "manage:crm.email",
  // `satisfies`, not a `Record<string, string>` annotation: the keys stay
  // literal, so a tab whose path is misspelled at the registry is a compile
  // error rather than a tab that quietly hides from everybody.
} satisfies Record<string, string>;

/**
 * The Payments side menu.
 *
 * Both tabs answer to `manage:crm.sales`, and neither gets a key of its own.
 * That right already writes the seller's IBAN under Invoices, and both of these
 * are the same decision one level up: which ways this desk lets an invoice be
 * settled, and the account behind the one that takes a card. A key per panel
 * here would be two more things to remember when a role is created, for an
 * audience that is in both cases the person who already runs the invoicing.
 *
 * The WRITE key for both, even though the reads are deliberately wider
 * (`view:crm`, because the payment dialog on every invoice offers this list to
 * anybody who may record a payment). Each panel is a form whose every control
 * saves, and a tab that can only ever 403 is the failure this file exists to
 * prevent.
 */
export const PAYMENT_TAB_PERM = {
  // GET and PUT /api/admin/crm/payment-settings (settings/crm-payments.routes.ts).
  methods: "manage:crm.sales",
  // GET/PUT /api/admin/crm/billing/settings and the test call (crm-billing).
  "card-payments": "manage:crm.sales",
} satisfies Record<string, string>;

/**
 * The Phone side menu.
 *
 * All but one of these read and write the desk's own configuration - the
 * credentials, the hours, the spoken notice - so they answer to
 * `manage:crm.calls`. Since the §3.8 split that key means exactly this and
 * nothing else: it no longer lets anybody dial, and it no longer opens the call
 * log. Reading a colleague's call is `read:crm.calls.recordings`, and it does
 * not belong on a Settings tab.
 *
 * The one exception is `busy`: a person's own choice of what a second call does
 * during a call. It answers to `use:crm.calls`, the key for being on the phone,
 * because the owner wants it in Settings next to the call settings (2026-11-02)
 * and the people it belongs to are the ones who take calls. It also opens
 * Settings for every call operator, with this one tab in it.
 *
 * They are listed one by one anyway rather than collapsed into a single
 * constant, because the routes behind them are not one route, and the day one
 * of them grows its own right this list is where it says so.
 */
export const PHONE_TAB_PERM = {
  // GET and PUT /api/admin/crm/calls/settings (crm-calls), plus the Twilio test.
  telephony: "manage:crm.calls",
  "caller-id": "manage:crm.calls",
  // Answering the Twilio number; same GET/PUT /api/admin/crm/calls/settings row.
  inbound: "manage:crm.calls",
  // The keys a caller may press before anybody is rung: the same GET/PUT
  // /api/admin/crm/calls/settings row, plus the clip synthesis its two
  // questions are recorded with.
  menu: "manage:crm.calls",
  // Working hours: the same settings row, plus POST .../calls/hours/preview.
  hours: "manage:crm.calls",
  // Sending calls elsewhere before anybody is rung: the same settings row for
  // the rules, plus GET/PUT /api/admin/crm/calls/divert for the switch itself,
  // which the call log throws from its own rail on the same permission.
  forwarding: "manage:crm.calls",
  // GET and PUT /api/admin/crm/push/settings (crm-push/routes.ts): the Firebase key
  // the phone app's notices go out with, on the same right as the Twilio keys.
  "app-push": "manage:crm.calls",
  // GET and PUT /api/admin/crm/calls/busy-mode (crm-calls/busy-mode.routes.ts).
  // The caller's own row only, so the operator key is enough - see above.
  busy: "use:crm.calls",
  transcription: "manage:crm.calls",
  // The spoken notice; same GET/PUT /api/admin/crm/calls/settings row.
  notice: "manage:crm.calls",
  // GET/PUT /api/admin/crm/calls/settings and .../pilot/checklist (crm-calls).
  pilot: "manage:crm.calls",
  // Calls AI, the machine that answers the phone itself: the `aiAgent` group
  // of the same GET/PUT /api/admin/crm/calls/settings row, plus GET/PUT
  // .../calls/agent/knowledge, GET .../agent/knowledge/draft and GET
  // .../agent/recent (crm-calls/agent.routes.ts). One key for all of it: the
  // knowledge text is what the machine says to callers, which is the same
  // decision as switching it on.
  "ai-agent": "manage:crm.calls",
  // Calls AI placing calls itself: GET/PUT /api/admin/crm/calls/ai-outbound/settings
  // (crm-calls/outbound.routes.ts). The company card's "AI test call" answers to
  // the same key on POST .../ai-outbound/test-call, but it is a button on the
  // register page, not a tab, so it is gated there rather than listed here.
  "ai-outbound": "manage:crm.calls",
  "recording-storage": "manage:crm.calls",
} satisfies Record<string, string>;

/** Everything the Integrations tab is worth opening for. */
export const INTEGRATION_PERMS: string[] = [...new Set(Object.values(INTEGRATION_TAB_PERM))];

/** Everything the Payments tab is worth opening for. */
export const PAYMENT_PERMS: string[] = [...new Set(Object.values(PAYMENT_TAB_PERM))];

/** Everything the Phone tab is worth opening for. */
export const PHONE_PERMS: string[] = [...new Set(Object.values(PHONE_TAB_PERM))];

/**
 * The Settings tabs themselves.
 *
 * `invoicing` is gated on the WRITE right, not on `view:crm` which the GET
 * accepts. Numbering templates and the seller's own details are a form: a
 * viewer who cannot save is being shown a page whose only purpose is saving.
 *
 * `labels` is gated on the two write rights and NOT on `view:crm`, even though
 * the read route accepts it. Holding one of the two is enough - that operator
 * edits their own desk's vocabulary and reads the other, which is what
 * `LabelSettings` already renders. Adding `view:crm` here would put a Settings
 * entry in every CRM viewer's sidebar for a page on which every control is
 * disabled.
 */
export const SETTINGS_TAB_PERM = {
  // PUT /api/admin/crm/numbering and /company (crm-sales, manage:crm.sales).
  invoicing: ["manage:crm.sales"],
  // The two payment panels - see `PAYMENT_TAB_PERM` above. Second, right behind
  // Invoices, because the two are the same document seen from opposite ends:
  // Invoices says what the paper prints, this says how the reader is allowed to
  // settle it.
  payments: PAYMENT_PERMS,
  // PUT /api/admin/crm/branding (settings/crm-branding.routes.ts).
  appearance: ["manage:settings"],
  // PUT /api/admin/crm/labels/mail and /calls (settings/crm-labels.routes.ts).
  labels: ["manage:crm.email", "manage:crm.calls"],
  // PUT /api/admin/crm/support/sla (settings/crm-sla.routes.ts).
  //
  // The WRITE key, even though the matching GET is deliberately wider
  // (`view:crm`, because the SLA state is drawn on every ticket row and the
  // person reading a red marker needs to be able to find out what it means).
  // The tab is a panel of eight editable fields; opening it for somebody whose
  // every keystroke would 403 is the failure this file exists to prevent, and
  // the panel drops its inputs for a reader anyway.
  support: ["manage:crm.support"],
  // PUT /api/admin/crm/portal-defaults (settings/crm-portal.routes.ts). The
  // WRITE key, and the SAME one the per-client and per-contact grants already
  // use: who sees what in the client portal is the clients desk's decision at a
  // wider radius, not a different kind of decision, so it gets no key of its
  // own. The matching GET is deliberately wider (`view:crm`) because the client
  // detail route echoes these defaults to anybody who can read a client - but
  // the tab is a panel whose every control writes, so it is gated on the write.
  portal: ["manage:crm.clients"],
  integrations: INTEGRATION_PERMS,
  phone: PHONE_PERMS,
  roles: ["manage:roles"],
  // GET /api/admin/crm/logs, /logs/facets and /logs/staff (crm-audit/routes.ts).
  //
  // A READ key, and the only tab here gated on one. Every other entry above is
  // a write key because its panel is a form; this panel has no form at all -
  // the ledger has no write route and must not grow one. The right question for
  // this tab is therefore "may this person see what their colleagues did", and
  // `read:crm.logs` is the key that asks exactly that.
  logs: ["read:crm.logs"],
  // GET /api/admin/crm/import/status, /introspect and /runs, POST .../run and
  // .../revert (crm-import/perfex). One key, and the WRITE key, because there
  // is no viewer half of this tab: reading the source opens a connection to the
  // old CRM's database, and the only reason to look is to decide whether to
  // press something. A tab whose every control refuses is the 403 this file
  // exists to prevent.
  import: ["manage:crm.import"],
} satisfies Record<string, string[]>;

/**
 * What puts `/settings` in the sidebar and mounts its route.
 *
 * The union, derived rather than typed out again: a tab added above without a
 * matching key here would be reachable by URL and invisible in the nav, and a
 * key left here after a tab is removed would leave a door onto an empty page.
 */
export const SETTINGS_PERMS: string[] = [...new Set(Object.values(SETTINGS_TAB_PERM).flat())];
